import { logger, sanitizeError } from '@heidi/runtime/logger';
import { TemplateVisibility } from '@heidi/schema/ml-scribe';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { createTemplate } from '../api/templates';
import { invalidateAllTemplatesQuery } from '../invalidate';

type Props = {
  templateId: string;
};

/**
 * Ports, each fired exactly where its app effect fired before (ARCFE-921).
 *
 * The COMPLIANCE CLASSIFICATION stays in the package — deciding that a 4xx body
 * is a `ComplianceCheckFailedError`, and pulling the noncompliant length out of
 * it, is reading the wire contract. Only the consequences (an analytics event,
 * a modal, two toasts) are app reactions.
 */
export function useMutateImportTemplateFromGallery({
  onImported,
  onComplianceWarning,
  onImportFailed,
}: {
  onImported?: (sourceTemplateId: string | null | undefined) => void;
  onComplianceWarning?: (info: {
    complianceOutputLength: number | undefined;
    templateId: string;
  }) => void;
  onImportFailed?: () => void;
} = {}) {
  return useMutation({
    mutationKey: ['createTemplate'],
    mutationFn: async ({ input, saveAnyway }: { input: Props; saveAnyway: boolean }) => {
      const data = await createTemplate({
        template: {
          source_template_id: input.templateId,
          trigger: 'from_template_gallery',
          template_visibility: TemplateVisibility.User,
        },
        saveAnyway: saveAnyway,
      });

      return data;
    },
    onSuccess: async ({ data }) => {
      await invalidateAllTemplatesQuery();
      onImported?.(data.source_template_id);
    },
    onError: (error, variables) => {
      if (error instanceof AxiosError && error.response?.data) {
        const errorData = error.response.data as {
          detail: {
            type: string;
            noncompliant_input: string;
            passed_compliance_check: boolean;
          };
        };
        if (errorData.detail.type === 'ComplianceCheckFailedError') {
          onComplianceWarning?.({
            complianceOutputLength: errorData.detail.noncompliant_input?.length,
            templateId: variables.input.templateId,
          });
          return;
        }
      }
      logger.error('[TEMPLATE_GALLERY_IMPORT] import failed', {
        error: sanitizeError(error),
      });
      onImportFailed?.();
    },
  });
}
